import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  message, Button, Input, Popover, DatePicker, Pagination, Dropdown,
  Modal, Spin, Empty, Tooltip, Badge, Drawer, Tag, Tabs
} from 'antd';
import {
  PlusOutlined, CodeOutlined, ReloadOutlined, SearchOutlined, FilterOutlined,
  MoreOutlined, CheckCircleOutlined, PauseCircleOutlined, RollbackOutlined,
  CloseCircleOutlined, ClockCircleOutlined, InboxOutlined, FolderOpenOutlined, RightOutlined,
  HolderOutlined, StarFilled, StarOutlined, ColumnHeightOutlined, CopyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { hasPermission } from './proformaHelpers';
import { proformaApi } from './proformaApi';
import ProformaJsonModal from './ProformaJsonModal';
import ProformaPrintButton from './ProformaPrintButton';
import ProformaActivity from './ProformaActivity';
import RecallModal from './RecallModal';
import {
  PROFORMA_TABS, ATTENTION_TABS, isValidTabKey, SEARCH_DEBOUNCE_MS,
  PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE,
  buildListParams, buildCountsParams, pageInfo,
  rowPrimary, rowBadge, rowActions, rowAmount, rowCustomerName, rowLocation,
  rowGstin, rowSalesperson, rowMobile, rowChargeSqmLabel, rowQtyLabel,
  rowJobType, rowConfirmedOnLabel, rowPfDateLabel, rowPreparedBy, rowProformaNo,
  rowWoNo, rowWoDateLabel, rowInvoiceBadge,
  customerInitials, formatMoney
} from './proformaListHelpers';
import './proformaList.css';

const EMPTY_JSON = { open: false, title: '', proformaNo: '', data: null, loading: false, error: null };
const EMPTY_COUNTS = { PENDING: 0, CONFIRMED: 0, HOLD: 0, CANCELLED: 0, ALL: 0 };
const REASON_INIT = { open: false, action: null, record: null, reason: '', busy: false };

const ACTION_ICON = {
  confirm: <CheckCircleOutlined />,
  hold: <PauseCircleOutlined />,
  reopen: <RollbackOutlined />,
  cancel: <CloseCircleOutlined />
};

// Lifecycle icon per tab (Gmail-style leading glyph): awaiting → clock,
// confirmed → check, on hold → pause, cancelled → cross, all → inbox.
const TAB_ICON = {
  PENDING: <ClockCircleOutlined />,
  CONFIRMED: <CheckCircleOutlined />,
  HOLD: <PauseCircleOutlined />,
  CANCELLED: <CloseCircleOutlined />,
  ALL: <InboxOutlined />
};

// An order flagged Express Delivery (the "Order Details & Summary" toggle,
// persisted on sales_proformas.express_delivery) renders with a gold priority
// star in the list. Replaces the earlier pseudo-random visual stub.
function isExpressDelivery(row) {
  return !!(row && row.expressDelivery);
}

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}

// ── Stage SALES-PROFORMA-LIST-UX-1 — Gmail-style Proforma list ───────
// Server-side tabs + pagination + counts (no more client-side limit:200
// dump). URL syncs ?status / ?page / ?q so a filtered view is shareable +
// survives a refresh. Editor / calculation / GST / lifecycle rules are
// untouched — this is a read + transition surface only.
export default function ProformaListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Stage SALES-PROFORMA-UI-FLOW-1 — gate "New Proforma" on the create
  // permission (backend stays the source of truth). Master → `*` wildcard.
  const { me } = useAuth();
  const canCreate = hasPermission(me && me.permissions, 'erp.sales.proforma.create');

  // ── URL-synced view state ──
  const statusKey = (() => {
    const s = (searchParams.get('status') || 'PENDING').toUpperCase();
    return isValidTabKey(s) ? s : 'PENDING';
  })();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const urlQuery = searchParams.get('q') || '';

  // Page size is a client preference (not shared via URL).
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Row density — 'compact' (reduced line-item type) or 'comfortable' (the
  // previous baseline). Persisted locally; the toolbar toggle flips it so the
  // current sizes can always be restored.
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('glassi.pfDensity') === 'comfortable' ? 'comfortable' : 'compact'; }
    catch (_e) { return 'compact'; }
  });
  const compact = density === 'compact';
  const toggleDensity = useCallback(() => {
    setDensity((d) => {
      const next = d === 'compact' ? 'comfortable' : 'compact';
      try { localStorage.setItem('glassi.pfDensity', next); } catch (_e) { /* non-critical */ }
      return next;
    });
  }, []);
  // Search box is a controlled local value, debounced into the URL `q`.
  const [searchInput, setSearchInput] = useState(urlQuery);
  // Date-range filter (dayjs pair) → server dateFrom/dateTo.
  const [dateRange, setDateRange] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const dateFrom = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
  const dateTo = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;

  // ── URL helpers ──
  const patchParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '') next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStatus = useCallback((key) => {
    patchParams({ status: key, page: 1 });
  }, [patchParams]);

  const setPage = useCallback((p) => patchParams({ page: p }), [patchParams]);

  // ── Debounce the search box into the URL (server-side search) ──
  useEffect(() => {
    const t = setTimeout(() => {
      if ((searchInput || '') !== (urlQuery || '')) {
        patchParams({ q: searchInput || '', page: 1 });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, urlQuery, patchParams]);

  // Keep the box in sync if the URL changes externally (back/forward).
  useEffect(() => { setSearchInput((v) => (v === urlQuery ? v : urlQuery)); }, [urlQuery]);

  // ── Data fetch (rows + counts) ──
  // A monotonic token guards against out-of-order responses clobbering a
  // newer request (fast typing / tab switching).
  const reqRef = useRef(0);
  const context = useMemo(() => ({ search: urlQuery, dateFrom, dateTo }), [urlQuery, dateFrom, dateTo]);

  const fetchRows = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true);
    try {
      const params = buildListParams({ status: statusKey, search: urlQuery, page, pageSize, dateFrom, dateTo });
      const data = await proformaApi.list(params);
      if (token !== reqRef.current) return;   // a newer request won
      const list = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
      setRows(list);
      setTotal(data && Number.isFinite(data.total) ? data.total : list.length);
    } catch (err) {
      if (token === reqRef.current) message.error(backendError(err, 'Failed to load proformas.'));
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [statusKey, urlQuery, page, pageSize, dateFrom, dateTo]);

  const fetchCounts = useCallback(async () => {
    try {
      const c = await proformaApi.statusCounts(buildCountsParams(context));
      setCounts(c && typeof c === 'object' ? { ...EMPTY_COUNTS, ...c } : EMPTY_COUNTS);
    } catch (_e) {
      // Counts are non-critical chrome — a failure just shows no badges.
      setCounts(EMPTY_COUNTS);
    }
  }, [context]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const refresh = useCallback(() => { fetchRows(); fetchCounts(); }, [fetchRows, fetchCounts]);

  const openEdit = useCallback((record) => {
    if (record && record.id) navigate(`/erp/sales/proformas/${record.id}`);
  }, [navigate]);

  // Stage SALES-PROFORMA-CLONE-1 — duplicate a proforma into a new PENDING draft
  // (all content copied server-side) and open it in the editor. Non-destructive;
  // a light confirm avoids an accidental copy. Clonable from any status.
  const onClone = useCallback((record) => {
    if (!record || !record.id) return;
    const pf = record.proformaNo || 'this proforma';
    Modal.confirm({
      title: 'Duplicate proforma',
      icon: <CopyOutlined />,
      content: `Create a new editable draft copied from ${pf}? All line items and details are copied; the new proforma starts as Pending with a fresh number.`,
      okText: 'Duplicate',
      onOk: async () => {
        const hide = message.loading(`Duplicating ${pf}…`, 0);
        try {
          const created = await proformaApi.clone(record.id);
          hide();
          message.success(`Created ${created && created.proformaNo ? created.proformaNo : 'a copy'} from ${pf}.`);
          if (created && created.id) navigate(`/erp/sales/proformas/${created.id}`);
          else refresh();
        } catch (err) {
          hide();
          message.error(backendError(err, 'Could not duplicate the proforma.'));
          throw err;   // keep the confirm dialog open on failure
        }
      }
    });
  }, [navigate, refresh]);

  // ── Detail drawer (read-only record view) ──
  const [detailRow, setDetailRow] = useState(null);
  const openDetail = useCallback((record) => setDetailRow(record), []);
  const closeDetail = useCallback(() => setDetailRow(null), []);
  // WO-3.1 — pre-cut Recall (opened from the drawer for a CONFIRMED order; the
  // backend refuses if not released or if cutting has already started).
  const [recallRecord, setRecallRecord] = useState(null);

  // ── Saved PI JSON inspector (read-only) ──
  const [jsonState, setJsonState] = useState(EMPTY_JSON);
  const closeJson = useCallback(() => setJsonState(EMPTY_JSON), []);
  const openJson = useCallback(async (record) => {
    if (!record || !record.id) return;
    const label = record.proformaNo || record.id;
    setJsonState({ open: true, title: `Proforma Billing JSON — ${label}`, proformaNo: record.proformaNo || '', data: null, loading: true, error: null });
    try {
      const full = await proformaApi.get(record.id);
      setJsonState((s) => (s.open ? { ...s, data: full, loading: false } : s));
    } catch (err) {
      setJsonState((s) => (s.open ? { ...s, loading: false, error: backendError(err, 'Failed to load proforma JSON.') } : s));
    }
  }, []);

  // ── Lifecycle quick-actions ──
  const [reasonModal, setReasonModal] = useState(REASON_INIT);

  const runTransition = useCallback(async (action, record, reason) => {
    try {
      if (action === 'confirm') await proformaApi.confirm(record.id);
      else if (action === 'reopen') await proformaApi.reopen(record.id);
      else if (action === 'hold') await proformaApi.hold(record.id, reason);
      else if (action === 'cancel') await proformaApi.cancel(record.id, reason);
      // Stage APPROVAL-1 — the drawer confirm/hold/cancel IS the L1 approval, so
      // the status changes immediately. A confirm under the Accounts (L1_L2)
      // policy then opens an Accounts review before the Work Order can release.
      const pf = record.proformaNo || 'Proforma';
      const msg = ({
        confirm: `${pf} confirmed.`,
        hold:    `${pf} put on hold.`,
        cancel:  `${pf} cancelled.`,
        reopen:  `${pf} moved to Pending.`
      })[action];
      message.success(msg);
      refresh();
      return true;
    } catch (err) {
      message.error(backendError(err, 'Could not update the proforma.'));
      return false;
    }
  }, [refresh]);

  const onQuickAction = useCallback((action, record) => {
    // Hold + Cancel require an operator reason (Hold is mandatory per lifecycle).
    if (action === 'hold' || action === 'cancel') {
      setReasonModal({ open: true, action, record, reason: '', busy: false });
      return;
    }
    runTransition(action, record);
  }, [runTransition]);

  const submitReason = useCallback(async () => {
    const { action, record, reason } = reasonModal;
    if (action === 'hold' && !reason.trim()) {
      message.warning('A hold reason is required.');
      return;
    }
    setReasonModal((s) => ({ ...s, busy: true }));
    const ok = await runTransition(action, record, reason.trim());
    if (ok) setReasonModal(REASON_INIT);
    else setReasonModal((s) => ({ ...s, busy: false }));
  }, [reasonModal, runTransition]);

  const info = useMemo(() => pageInfo(total, page, pageSize), [total, page, pageSize]);

  // ── Render ──
  const filtersContent = (
    <div className="w-72 p-1">
      <div className="text-xs font-medium text-ink-600 mb-1.5">Proforma date range</div>
      <DatePicker.RangePicker
        className="w-full"
        size="small"
        value={dateRange}
        allowClear
        format="YYYY-MM-DD"
        onChange={(vals) => { setDateRange(vals && vals.length ? vals : null); setPage(1); }}
      />
      <div className="flex justify-end mt-3">
        <Button
          size="small" type="text"
          disabled={!dateRange}
          onClick={() => { setDateRange(null); setPage(1); }}
        >
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <ErpMasterShell>
      <div className={`pf-list flex flex-col h-full min-h-0${compact ? ' pf-compact' : ''}`}>
        {/* ── Sticky toolbar ── */}
        <div className="sticky top-0 z-20 bg-white border-b border-surface-200 px-5 py-2">
          <div className="pf-measure flex items-center gap-2">
          <div className="flex-1 min-w-0 max-w-md">
            <Input
              allowClear
              size="middle"
              prefix={<SearchOutlined className="text-ink-400" />}
              placeholder="Search proforma no, customer or GSTIN…"
              aria-label="Search proformas"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <Popover
            content={filtersContent}
            title={null}
            trigger="click"
            placement="bottomRight"
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
          >
            <Badge dot={!!dateRange} offset={[-2, 2]}>
              <Button
                size="middle"
                icon={<FilterOutlined />}
                aria-label="Filters"
              >
                Filters
              </Button>
            </Badge>
          </Popover>
          <Tooltip title={compact ? 'Switch to comfortable rows' : 'Switch to compact rows'}>
            <Button
              size="middle"
              icon={<ColumnHeightOutlined />}
              type={compact ? 'primary' : 'default'}
              ghost={compact}
              onClick={toggleDensity}
              aria-label={compact ? 'Switch to comfortable row density' : 'Switch to compact row density'}
              aria-pressed={compact}
            />
          </Tooltip>
          <Tooltip title="Refresh">
            <Button size="middle" icon={<ReloadOutlined />} onClick={refresh} aria-label="Refresh list" />
          </Tooltip>
          {canCreate ? (
            <Button
              type="primary" size="middle" icon={<PlusOutlined />}
              onClick={() => navigate('/erp/sales/proformas/new')}
            >
              New Proforma
            </Button>
          ) : null}
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="border-b border-surface-200 bg-white px-5 py-1.5">
        <div
          className="pf-measure pf-tabs flex items-center gap-1.5 overflow-x-auto"
          role="tablist"
          aria-label="Proforma status"
        >
          {PROFORMA_TABS.map((tab) => {
            const active = tab.key === statusKey;
            const count = counts[tab.countKey] || 0;
            const attention = ATTENTION_TABS.has(tab.key) && count > 0;
            // Count emphasis: active → solid pill; inactive attention → soft
            // tinted pill; everything else → plain muted number.
            const countVariant = active ? '--active' : (attention ? '--soft' : '--muted');
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatus(tab.key)}
                className={`pf-tab pf-tab-${tab.key}`}
              >
                <span className="pf-tab-icon" aria-hidden="true">{TAB_ICON[tab.key]}</span>
                <span>{tab.label}</span>
                <span
                  className={`pf-tab-count pf-tab-count${countVariant}`}
                  aria-label={attention && !active ? `${count} need attention` : undefined}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        </div>

        {/* ── Column header (aligns with the row columns; stays put while the
            list scrolls beneath it) ── */}
        {rows.length > 0 && (
          <div className="border-b border-surface-200 bg-surface-50 px-5">
            <div className="pf-measure pf-t-hdr flex items-center gap-2.5 py-1.5 pl-2 font-semibold uppercase tracking-wide text-ink-400">
              <span className="w-[16px] shrink-0 text-center" aria-hidden="true">
                <StarFilled className="pf-priority-star" />
              </span>
              <span className="w-[14px] shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">Customer</div>
              <div className="hidden lg:block w-[150px] shrink-0">Proforma · Date</div>
              <div className="hidden xl:block w-[150px] shrink-0">Work Order · Date</div>
              <div className="hidden lg:block w-[140px] shrink-0">Mobile · Sales</div>
              <div className="hidden md:block w-[88px] shrink-0 text-right">SqMt · Qty</div>
              <div className="w-[172px] shrink-0 text-right">Value · Prepared by</div>
              <span className="w-[13px] shrink-0" aria-hidden="true" />
            </div>
          </div>
        )}

        {/* ── List body ── */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-white" role="list">
          {loading && rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 h-full py-24 text-ink-400">
              <Spin />
              <span className="text-xs">Loading proformas…</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center h-full py-24">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className="text-ink-500">
                    <div className="font-medium text-ink-700">No proformas found</div>
                    <div className="text-sm mt-0.5">
                      {urlQuery || dateRange ? 'Try clearing the search or filters.' : 'Create your first proforma invoice.'}
                    </div>
                  </div>
                }
              >
                {canCreate && !urlQuery && !dateRange && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/erp/sales/proformas/new')}>
                    New Proforma
                  </Button>
                )}
              </Empty>
            </div>
          ) : (
            <Spin spinning={loading}>
              <ul className="pf-rows px-5">
                {rows.map((r) => (
                  <ProformaRow
                    key={r.id}
                    row={r}
                    onOpen={openEdit}
                    onJson={openJson}
                    onAction={onQuickAction}
                    onClone={onClone}
                    canClone={canCreate}
                    onExpand={openDetail}
                  />
                ))}
              </ul>
            </Spin>
          )}
        </div>

        {/* ── Footer: pagination ── */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-surface-200 px-5 py-2">
          <div className="pf-measure flex items-center justify-between">
            <div className="text-xs text-ink-500 tabular-nums">{info.label}</div>
            <Pagination
              size="small"
              current={info.page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onChange={(p, ps) => {
                if (ps !== pageSize) { setPageSize(ps); setPage(1); }
                else setPage(p);
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Detail drawer — a professional record panel, slid in from the right. ── */}
      <ProformaDetailDrawer row={detailRow} onClose={closeDetail} onOpen={openEdit} onAction={onQuickAction}
        onChangeOrder={(row) => { closeDetail(); navigate(`/erp/sales/proformas/${row.id}/change-order`); }}
        onRecall={setRecallRecord} />

      {/* ── WO-3.1 · pre-cut Recall ── */}
      <RecallModal
        open={!!recallRecord}
        proforma={recallRecord}
        onClose={() => setRecallRecord(null)}
        onDone={(res) => {
          setRecallRecord(null);
          refresh();
          // Reopened to an editable PENDING draft — jump straight into the editor
          // so the user can change the sizes, then re-confirm + re-release.
          if (res && res.id) navigate(`/erp/sales/proformas/${res.id}`);
        }}
      />

      <ProformaJsonModal
        open={jsonState.open}
        title={jsonState.title}
        proformaNo={jsonState.proformaNo}
        data={jsonState.data}
        loading={jsonState.loading}
        error={jsonState.error}
        onClose={closeJson}
      />

      {/* ── Hold / Cancel reason modal ── */}
      <Modal
        open={reasonModal.open}
        title={reasonModal.action === 'hold' ? 'Put proforma on hold' : 'Cancel proforma'}
        okText={reasonModal.action === 'hold' ? 'Put on hold' : 'Cancel proforma'}
        okButtonProps={{ danger: reasonModal.action === 'cancel', loading: reasonModal.busy }}
        cancelText="Back"
        onOk={submitReason}
        onCancel={() => setReasonModal(REASON_INIT)}
        destroyOnHidden
      >
        <div className="text-sm text-ink-600 mb-2">
          {reasonModal.record ? (reasonModal.record.proformaNo || reasonModal.record.id) : ''}
          {reasonModal.action === 'hold'
            ? ' — a hold reason is required.'
            : ' — add an optional cancellation reason.'}
        </div>
        <Input.TextArea
          rows={3}
          autoFocus
          value={reasonModal.reason}
          onChange={(e) => setReasonModal((s) => ({ ...s, reason: e.target.value }))}
          placeholder={reasonModal.action === 'hold' ? 'Reason for hold (required)' : 'Reason for cancellation (optional)'}
          aria-label="Reason"
        />
      </Modal>
    </ErpMasterShell>
  );
}

// ── Detail drawer building blocks ───────────────────────────────────
// Lifecycle accent hex (matches the tabs + row rails), used for the header
// wash, avatar tint, and the hero accent rule.
const STATUS_HEX = { PENDING: '#1877f2', CONFIRMED: '#16a34a', HOLD: '#ea8600', CANCELLED: '#dc2626' };

// One label→value line. Empty → a faint dash so real data reads first.
function DrawerField({ label, value }) {
  const has = value != null && value !== '';
  return (
    <div className="pf-dd-field">
      <dt>{label}</dt>
      <dd className={has ? '' : 'is-empty'} title={has ? String(value) : ''}>{has ? value : '—'}</dd>
    </div>
  );
}

// A titled section: eyebrow + hairline rule + definition list.
function DrawerSection({ title, rows }) {
  return (
    <div className="pf-dd-section">
      <div className="pf-dd-section-hd">
        <span className="pf-detail-eyebrow">{title}</span>
        <span className="rule" aria-hidden="true" />
      </div>
      <dl className="pf-dd-dl">
        {rows.map((r) => <DrawerField key={r.label} label={r.label} value={r.value} />)}
      </dl>
    </div>
  );
}

// One metric tile — a big figure + caption. `empty` renders a faint dash.
function DrawerTile({ value, unit, caption, empty }) {
  return (
    <div className="pf-dd-tile">
      <div className={`pf-dd-tile-v${empty ? ' is-empty' : ''}`}>
        {empty ? '—' : <>{value}{unit ? <span className="u">{unit}</span> : null}</>}
      </div>
      <div className="pf-dd-tile-k">{caption}</div>
    </div>
  );
}

function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

// The quotation's real lifecycle IS a sequence, so it renders as a path — not a
// flat list. Work order / Invoice / Delivery are future stages (not built yet);
// they read as the road ahead rather than empty rows.
const LIFECYCLE_STEPS = ['Pending', 'Confirmed', 'Work order', 'Invoice', 'Delivery'];
function lifecycleReached(status) { return status === 'CONFIRMED' ? 1 : 0; }

function LifecyclePath({ status }) {
  if (status === 'CANCELLED') {
    return <div className="pf-dd-path pf-dd-path--void">Cancelled — this quotation is closed.</div>;
  }
  const reached = lifecycleReached(status);
  return (
    <ol className="pf-dd-path" aria-label="Quotation lifecycle">
      {LIFECYCLE_STEPS.map((label, i) => {
        const state = i < reached ? 'done' : i === reached ? 'now' : 'todo';
        return (
          <li key={label} className={`pf-dd-step is-${state}`}>
            <span className="pf-dd-dot" aria-hidden="true" />
            <span className="pf-dd-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// One compact workflow fact (label over value); em dash when empty.
function DrawerFact({ label, value }) {
  const empty = value == null || value === '';
  return (
    <div className="pf-dd-fact">
      <div className="pf-dd-fact-k">{label}</div>
      <div className={`pf-dd-fact-v${empty ? ' is-empty' : ''}`}>{empty ? '—' : value}</div>
    </div>
  );
}

// ── Detail drawer — a "glass spec sheet" record panel (redesign) ─────
// A full-height status edge rail (the tinted edge of a glass sheet) carries the
// lifecycle colour; a statement hero answers "how much", a connected spec strip
// answers "how big", quiet ledgers carry the rest, and a lifecycle PATH shows
// where the quotation sits. Nothing from the prior drawer is dropped.
function ProformaDetailDrawer({ row, onClose, onOpen, onAction, onChangeOrder, onRecall }) {
  const has = !!row;
  const accent = has ? (STATUS_HEX[row.status] || '#475569') : '#475569';
  const badge = has ? rowBadge(row) : { color: 'default', label: '' };
  const customer = has ? rowCustomerName(row) : '';

  const areaN = has ? toNum(row.totalChargeSqm) : 0;
  const qtyN = has ? toNum(row.totalQty) : 0;
  const jobType = has ? rowJobType(row) : '';
  // Stage SALES-PROFORMA-APPROVAL-1 — the lifecycle actions (Confirm / Hold /
  // Cancel / Move to Pending) live on THIS drawer's action panel now (moved off
  // the proforma editor). Valid transitions come from the shared status source
  // of truth; taking one closes the drawer and runs the same quick-action the
  // row menu uses. The drawer confirm IS the L1 approval (applied directly); a
  // confirm under the Accounts policy then opens the L2 review in the inbox.
  const acts = has ? rowActions(row) : { transitions: [] };
  const runAction = (key) => { const r = row; onClose(); if (onAction) onAction(key, r); };

  const sections = has ? [
    { title: 'Customer', rows: [
      { label: 'GSTIN', value: rowGstin(row) },
      { label: 'Mobile', value: rowMobile(row) },
      { label: 'Location', value: rowLocation(row) },
      { label: 'Salesperson', value: rowSalesperson(row) }
    ] },
    { title: 'Document', rows: [
      { label: 'Proforma No', value: rowProformaNo(row) },
      { label: 'Date', value: rowPfDateLabel(row) },
      // WO-3.x — the released Work Order + its date (blank until released).
      { label: 'Work Order No', value: rowWoNo(row) },
      { label: 'Work Order Date', value: rowWoDateLabel(row) }
    ] }
    // Workflow is rendered below as a lifecycle PATH + facts, not a flat list.
  ] : [];

  return (
    <Drawer
      open={has}
      onClose={onClose}
      width={560}
      destroyOnHidden
      className="pf-detail-drawer"
      rootStyle={has ? { '--dd': accent } : undefined}
      styles={has ? {
        header: {
          background: `color-mix(in srgb, ${accent} 8%, #ffffff)`,
          borderBottom: `1px solid color-mix(in srgb, ${accent} 16%, #ffffff)`
        }
      } : undefined}
      title={has ? (
        <div className="pf-dd-head" style={{ '--dd': accent }}>
          <div className="pf-dd-avatar" aria-hidden="true">{customerInitials(customer)}</div>
          <div className="pf-dd-id">
            <div className="pf-dd-name" title={customer}>{customer}</div>
            <div className="pf-dd-sub">{rowProformaNo(row)} · {rowPfDateLabel(row)}</div>
          </div>
          <Tag color={badge.color} className="!m-0">{badge.label}</Tag>
        </div>
      ) : null}
      footer={has ? (
        <div className="flex justify-end gap-2">
          <Button icon={<FolderOpenOutlined />} onClick={() => { const r = row; onClose(); onOpen(r); }}>
            Open proforma
          </Button>
          <ProformaPrintButton proforma={row} />
        </div>
      ) : null}
    >
      {has && (
        <div className="pf-list pf-dd-wrap" style={{ '--dd': accent }}>
          <Tabs
            className="pf-dd-tabs"
            defaultActiveKey="details"
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <div className="pf-dd-body">
                    {/* Hero — how much. An editorial statement, not a boxed card;
                        the edge rail already carries the lifecycle colour. */}
                    <div className="pf-dd-hero">
                      <div className="pf-dd-hero-eyebrow">Grand total</div>
                      <div className="pf-dd-hero-amount">{formatMoney(row.grandTotal)}</div>
                      <div className="pf-dd-hero-break">
                        Taxable <b>{formatMoney(row.taxableAmount)}</b>
                        <span className="pf-dd-hero-sep" aria-hidden="true">·</span>
                        GST <b>{formatMoney(row.gstAmount)}</b>
                      </div>
                    </div>

                    {/* Spec strip — how big (a connected bar of glass specs). */}
                    <div className="pf-dd-spec">
                      <DrawerTile empty={!(areaN > 0)} value={areaN.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit="sqm" caption="Charge area" />
                      <DrawerTile empty={!(qtyN > 0)} value={qtyN.toLocaleString('en-IN')} unit={qtyN === 1 ? 'pc' : 'pcs'} caption="Pieces" />
                      <DrawerTile empty={!jobType} value={jobType} caption="Job type" />
                    </div>

                    {/* Customer / Document ledgers. */}
                    {sections.map((s) => <DrawerSection key={s.title} title={s.title} rows={s.rows} />)}

                    {/* Workflow — the lifecycle PATH + the concrete facts. Keeps
                        every prior field (Confirmed on / Prepared by / Delivery)
                        while showing where the quotation sits in its sequence. */}
                    <div className="pf-dd-section">
                      <div className="pf-dd-section-hd">
                        <span className="pf-detail-eyebrow">Workflow</span>
                        <span className="rule" aria-hidden="true" />
                      </div>
                      <LifecyclePath status={row.status} />
                      <div className="pf-dd-facts">
                        <DrawerFact label="Confirmed on" value={rowConfirmedOnLabel(row)} />
                        <DrawerFact label="Prepared by" value={rowPreparedBy(row)} />
                        <DrawerFact label="Delivery date" value="" />
                      </div>
                    </div>

                    {/* ── Lifecycle action panel (Stage APPROVAL-1) — the status
                        actions, moved here from the proforma editor. Only the
                        transitions valid for this status render. */}
                    {(acts.transitions.length || row.status === 'CONFIRMED') ? (
                      <div className="pf-dd-actions">
                        {acts.transitions.map((t) => (
                          <Button
                            key={t.key}
                            size="middle"
                            danger={t.key === 'cancel'}
                            type={t.key === 'confirm' ? 'primary' : 'default'}
                            icon={ACTION_ICON[t.key]}
                            onClick={() => runAction(t.key)}
                          >
                            {t.label}
                          </Button>
                        ))}
                        {/* WO-3.1 — pre-cut change. Withdraw the un-cut Work Order +
                            reopen to edit; the backend refuses if cutting has started
                            (→ use a Change Order). Shown for a CONFIRMED order. */}
                        {row.status === 'CONFIRMED' && typeof onRecall === 'function' ? (
                          <Button size="middle" icon={<RollbackOutlined />} onClick={() => onRecall(row)}>
                            Recall from production
                          </Button>
                        ) : null}
                        {/* WO-3.3 — post-cut change. Shown for a CONFIRMED (released-
                            eligible) order; the modal verifies a live WO + cut pieces
                            and otherwise points the user at Recall. */}
                        {row.status === 'CONFIRMED' && typeof onChangeOrder === 'function' ? (
                          <Button size="middle" danger onClick={() => onChangeOrder(row)}>
                            ⚙ Raise Change Order
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )
              },
              {
                key: 'activity',
                label: 'Activity / Comments',
                children: (
                  <div className="pf-dd-body pf-dd-body--activity">
                    <ProformaActivity proformaId={row.id} />
                  </div>
                )
              }
            ]}
          />
        </div>
      )}
    </Drawer>
  );
}

// ── One list row (memoized). A clean two-line summary; click (or the chevron)
//    opens the detail drawer with the full record. ──
const ProformaRow = memo(function ProformaRow({ row, onOpen, onJson, onAction, onClone, canClone, onExpand }) {
  const navigate = useNavigate();
  const primary = rowPrimary(row);
  const badge = rowBadge(row);
  const acts = rowActions(row);
  const priority = isExpressDelivery(row);   // gold star flags Express Delivery orders

  const menuItems = acts.transitions.map((t) => ({
    key: t.key,
    label: t.label,
    icon: ACTION_ICON[t.key],
    danger: t.key === 'cancel'
  }));

  // Identity sub-line: city + GSTIN (salesperson has its own column).
  const summaryBits = [rowLocation(row), rowGstin(row)].filter(Boolean);

  const expand = () => onExpand(row);
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); expand(); }
  };

  return (
    <li className={`pf-row pf-stripe-${badge.status || 'PENDING'}${priority ? ' pf-row--express' : ''}`}>
      <div
        className="pf-row-inner pf-row-open pf-measure relative flex items-center gap-2.5 py-1 pl-2"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        onClick={expand}
        onKeyDown={onKey}
        aria-label={`${primary.customer} — ${primary.proformaNo}.${priority ? ' Express delivery.' : ''} Show details`}
      >
        {/* Priority marker — a filled gold star flags an Express Delivery order
            (set on the Order Details & Summary toggle). */}
        <span className="w-[16px] shrink-0 text-center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={priority ? 'Express delivery' : 'Standard delivery'}>
            {priority
              ? <StarFilled className="pf-priority-star" aria-label="Express delivery order" />
              : <StarOutlined className="pf-priority-star pf-priority-star--off" aria-label="Standard delivery" />}
          </Tooltip>
        </span>

        {/* Drag-style grip — reveals on hover as a left-edge affordance. */}
        <HolderOutlined className="pf-grip shrink-0" aria-hidden="true" />

        {/* Identity — customer (hero) over city · GSTIN. */}
        <div className="flex-1 min-w-0 leading-tight">
          <div className="pf-t-name truncate font-semibold text-ink-900">{primary.customer}</div>
          <div className="pf-t-sub truncate text-ink-500 mt-0.5">
            {summaryBits.length ? summaryBits.join('  ·  ') : '—'}
          </div>
        </div>

        {/* Document — proforma no over date. */}
        <div className="hidden lg:block shrink-0 w-[150px] min-w-0 leading-tight">
          <div className="pf-t-col truncate text-ink-700 tabular-nums tracking-wide">{primary.proformaNo}</div>
          <div className="pf-t-sub text-ink-400 tabular-nums mt-0.5">{rowPfDateLabel(row)}</div>
        </div>

        {/* Work Order — WO no over release date; em dash when not yet released. */}
        <div className="hidden xl:block shrink-0 w-[150px] min-w-0 leading-tight">
          {rowWoNo(row)
            ? <>
                <div className="pf-t-col truncate text-ink-700 tabular-nums tracking-wide">{rowWoNo(row)}</div>
                <div className="pf-t-sub text-ink-400 tabular-nums mt-0.5">{rowWoDateLabel(row)}</div>
              </>
            : <div className="pf-t-col text-surface-300">—</div>}
        </div>

        {/* Invoice — derived, read-only badge (Phase 2). Distinct from the
            proforma lifecycle status. Clicking an existing invoice opens it. */}
        <div className="hidden xl:block shrink-0 w-[124px] min-w-0 leading-tight" onClick={(e) => e.stopPropagation()}>
          {(() => {
            const ib = rowInvoiceBadge(row);
            const tag = <Tag color={ib.color} className="!m-0">{ib.label}</Tag>;
            return ib.invoiceId
              ? <Tooltip title={ib.invoiceNo || 'Open Tax Invoice'}>
                  <a onClick={() => navigate(`/erp/sales/invoices/${ib.invoiceId}`)}>{tag}</a>
                </Tooltip>
              : tag;
          })()}
        </div>

        {/* Contact — mobile over salesperson. */}
        <div className="hidden lg:block shrink-0 w-[140px] min-w-0 leading-tight">
          <div className="pf-t-col truncate text-ink-700 tabular-nums">{rowMobile(row) || '—'}</div>
          <div className="pf-t-sub truncate text-ink-400 mt-0.5">{rowSalesperson(row) || '—'}</div>
        </div>

        {/* Size — charge area over pieces. */}
        <div className="hidden md:block shrink-0 w-[88px] text-right leading-tight">
          <div className="pf-t-col text-ink-700 tabular-nums">{rowChargeSqmLabel(row) || '—'}</div>
          <div className="pf-t-sub text-ink-400 tabular-nums mt-0.5">{rowQtyLabel(row)}</div>
        </div>

        {/* Value — amount (hero) over status; status swaps to the action bar on
            hover/focus (overlaid, no reflow). */}
        <div className="shrink-0 w-[172px] relative">
          <div className="pf-t-name text-right font-semibold text-ink-900 tabular-nums leading-tight">
            {rowAmount(row)}
          </div>
          <div className="relative h-[18px] mt-0.5">
            <div className="pf-row-meta pf-t-sub absolute inset-0 flex items-center justify-end">
              {/* Prepared-by username (who created it). Status lives in the tabs. */}
              {rowPreparedBy(row)
                ? <span className="truncate text-ink-600">{rowPreparedBy(row)}</span>
                : <span className="text-surface-300">—</span>}
            </div>
            <div className="pf-row-actions absolute inset-0 flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Open">
                <Button size="small" type="text" icon={<FolderOpenOutlined />} onClick={() => onOpen(row)} aria-label="Open" />
              </Tooltip>
              <ProformaPrintButton proforma={row} />
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    ...menuItems,
                    ...(canClone ? [{ key: 'clone', label: 'Duplicate', icon: <CopyOutlined /> }] : []),
                    ...((menuItems.length || canClone) ? [{ type: 'divider' }] : []),
                    { key: 'json', label: 'View JSON', icon: <CodeOutlined /> }
                  ],
                  onClick: ({ key, domEvent }) => {
                    if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                    if (key === 'json') onJson(row);
                    else if (key === 'clone') onClone(row);
                    else onAction(key, row);
                  }
                }}
              >
                <Button size="small" type="text" icon={<MoreOutlined />} aria-label="More actions" />
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Open-detail indicator (chevron points into the drawer). */}
        <RightOutlined className="pf-chev shrink-0 text-ink-300" aria-hidden="true" />
      </div>
    </li>
  );
});
